import React, { useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Modal } from '@/ui/Modal';
import { TextArea } from '@/ui/TextField/variants/TextArea';
import { Checkbox } from '@/ui/Checkbox';
import { FeedbackFileUpload } from '@/features/feedback/molecules/FeedbackFileUpload';
import { useSubmitFeedbackMutationRemote } from '@/store/v2';
import { SharedSnackbarContext, SnackbarType } from '@/component/SharedSnackbar/SharedSnackbar';
import { useLearningJourney } from '@/store/v2/journeys/useLearningJourney';
import { useSpaceByCurrentRealm } from '@/store/spaces';

const FileHintCaptionStyled = styled.div`
  ${({ theme }) => theme.typography.body3};
  color: ${({ theme }) => theme.colors.neutral.onContainer};
`;

const CallConsentCaptionStyled = styled.div`
  ${({ theme }) => theme.typography.body3};
  color: ${({ theme }) => theme.colors.neutral.onBackground};
`;

const TextAreaStyled = styled(TextArea)`
  height: 80px;
  align-self: stretch;
`;

const FeedbackFileUploadWrapper = styled.div`
  margin-top: 11px;
  align-self: stretch;
`;

const FeedbackInputContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const InputSectionStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: -22px;
`;

const ContentContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: -16px;
`;

const CallConsentContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const CheckboxContainerStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckboxLabelStyled = styled.label`
  ${({ theme }) => theme.typography.body2};
  color: ${({ theme }) => theme.colors.neutral.onBackground};
`;

type FeedbackModalProps = {
  open: boolean;
  onClose: () => void;
};

export const FeedbackModal = ({ open, onClose }: FeedbackModalProps): JSX.Element => {
  const [feedbackText, setFeedbackText] = useState('');
  const [consentToCall, setConsentToCall] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = useState<string | undefined>();

  const params = useParams<{ journeyId?: string }>();
  const journeyId = params.journeyId ? parseInt(params.journeyId, 10) : undefined;

  const { journey } = useLearningJourney({
    id: journeyId || 0,
    skip: !journeyId,
  });

  const { space: realmSpace } = useSpaceByCurrentRealm();

  const spaceId = journey?.spaceId ?? realmSpace?.id;

  const { openSnackbar } = useContext(SharedSnackbarContext);
  const [submitFeedback, { loading: submitting }] = useSubmitFeedbackMutationRemote();

  const isSubmitDisabled = !feedbackText.trim() || submitting;

  const handleClose = () => {
    setFeedbackText('');
    setConsentToCall(false);
    setAttachmentUrl(undefined);
    onClose();
  };

  const handleSubmit = async () => {
    if (!feedbackText.trim()) {
      return;
    }

    try {
      const result = await submitFeedback({
        variables: {
          input: {
            feedbackText: feedbackText.trim(),
            consentToCall,
            referrerUrl: window.location.href,
            attachmentUrl: attachmentUrl || undefined,
          },
        },
      });

      if (result.data?.submitFeedback.success) {
        openSnackbar({
          message: 'Thank you for your feedback! We really appreciate your input.',
          type: SnackbarType.SUCCESS,
          isDismissive: true,
        });
        handleClose();
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      openSnackbar({
        message: 'Failed to submit feedback. Please try again.',
        type: SnackbarType.DANGER,
      });
    }
  };

  const handleFileUploaded = (url: string) => {
    setAttachmentUrl(url);
  };

  const handleFileRemoved = () => {
    setAttachmentUrl(undefined);
  };

  return (
    <Modal open={open} onClose={handleClose} size="mobileLandscape">
      <Modal.Title>What would you like us to improve?</Modal.Title>

      <Modal.Contents>
        <ContentContainerStyled>
          <InputSectionStyled>
            <FileHintCaptionStyled>
              Bonus points if you attach a screenshot or video recording illustrating your suggestion.
            </FileHintCaptionStyled>

            <FeedbackInputContainerStyled>
              <TextAreaStyled
                value={feedbackText}
                onChange={(e) => setFeedbackText((e.target as HTMLTextAreaElement).value)}
                label=""
                fullWidth
              />

              <FeedbackFileUploadWrapper>
                <FeedbackFileUpload
                  spaceId={spaceId}
                  onFileUploaded={handleFileUploaded}
                  onFileRemoved={handleFileRemoved}
                  uploadedFileUrl={attachmentUrl}
                />
              </FeedbackFileUploadWrapper>
            </FeedbackInputContainerStyled>
          </InputSectionStyled>

          <CallConsentContainerStyled>
            <CallConsentCaptionStyled>
              {
                "If we don't fully understand your feedback, would you be open to a 20-minute video call on this topic where you explain your problem in more detail?"
              }
            </CallConsentCaptionStyled>

            <CheckboxContainerStyled>
              <Checkbox checked={consentToCall} onChange={(e) => setConsentToCall(e.target.checked)} />
              <CheckboxLabelStyled onClick={() => setConsentToCall(!consentToCall)}>
                {"Yes, I'm open to a call"}
              </CheckboxLabelStyled>
            </CheckboxContainerStyled>
          </CallConsentContainerStyled>
        </ContentContainerStyled>
      </Modal.Contents>

      <Modal.Actions>
        <Modal.Action action={handleClose} disabled={submitting}>
          Cancel
        </Modal.Action>
        <Modal.Action action={handleSubmit} primary disabled={isSubmitDisabled}>
          Submit feedback
        </Modal.Action>
      </Modal.Actions>
    </Modal>
  );
};
